import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  InputGroup,
  InputRightElement,
  Switch,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Formik, Form, FieldArray, getIn } from "formik";
import * as Yup from "yup";
import InputField from "../../TextInputField";
import { useEffect, useState } from "react";
import BackButton from "../../BackButton";
import { useNavigate, useParams } from "react-router-dom";
import DeleteButton from "../../DeleteButton";

import {
  getProductById,
  createProduct,
  updateProduct,
} from "@/services/consignado-product.service";
import { ConsignadoProduct } from "@/models/consignado-product.model";
import RiskClassificationsSelect from "../components/select/RiskClassificationsSelect";
import InsuranceCompaniesSelect from "../components/select/InsureCompaniesSelect";
import InsuranceCompanyProductsSelect from "../components/select/InsuranceCompanyProductsSelect";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";

// Validation schema com mensagens em PT-BR
const productValidationSchema = Yup.object().shape({
  nome: Yup.string()
    .required("Nome do produto é obrigatório")
    .min(1, "Nome deve ter pelo menos 3 caracteres"),
  comissao: Yup.number()
    .nullable()
    .typeError("Comissão deve ser um número")
    .min(0, "Comissão não pode ser negativa")
    .max(100, "Comissão não pode exceder 100%"),
  // comissao_sem_seguro: Yup.number()
  //   .nullable()
  //   .typeError("Comissão sem seguro deve ser um número")
  //   .min(0, "Comissão sem seguro não pode ser negativa")
  //   .max(100, "Comissão sem seguro não pode exceder 100%"),
  gt_comissao: Yup.number()
    .nullable()
    .typeError("GT Comissão deve ser um número")
    .min(0, "GT Comissão não pode ser negativa")
    .max(100, "GT Comissão não pode exceder 100%"),
  valor_minimo: Yup.number()
    .required("Valor mínimo é obrigatório")
    .typeError("Valor mínimo deve ser um número")
    .max(
      Yup.ref("valor_maximo_emprestimo"),
      "Valor mínimo deve ser menor ou igual ao valor máximo"
    ),
  valor_maximo_emprestimo: Yup.number()
    .required("Valor máximo é obrigatório")
    .typeError("Valor máximo deve ser um número")
    .min(100, "Valor máximo deve ser maior que 100")
    .min(
      Yup.ref("valor_minimo"),
      "Valor máximo deve ser maior ou igual ao valor mínimo"
    ),
  numero_parcelas: Yup.number()
    .required("Número de parcelas é obrigatório")
    .typeError("Número de parcelas deve ser um número")
    .min(1, "Número de parcelas deve ser no mínimo 1"),
  habilitado: Yup.boolean().required("Status é obrigatório"),
  // inadimplencia_esperada: Yup.number()
  //   .required("Inadimplência esperada é obrigatória")
  //   .typeError("Inadimplência esperada deve ser um número")
  //   .min(0, "Inadimplência não pode ser negativa")
  //   .max(100, "Inadimplência não pode exceder 100%"),
  margem_usada: Yup.number()
    .required("Margem é obrigatória")
    .typeError("Deve ser um número")
    .min(0, "Não pode ser negativa")
    .max(100, "Não pode exceder 100%"),
  porcentual_seguro: Yup.number()
    .required("Seguro é obrigatório")
    .typeError("Deve ser um número")
    .min(0, "Não pode ser negativo")
    .max(100, "Não pode exceder 100%"),
  rebate: Yup.number()
    .required("Rebate é obrigatório")
    .typeError("Deve ser um número")
    .min(0, "Não pode ser negativo")
    .max(100, "Não pode exceder 100%"),
  taxa_mensal: Yup.number()
    .required("Taxa mensal é obrigatória")
    .typeError("Deve ser um número")
    .moreThan(0, "Taxa mensal deve ser maior que 0"),
  cet: Yup.number()
    .required("CET é obrigatório")
    .typeError("Deve ser um número")
    .moreThan(0, "CET deve ser maior que 0"),
  tir_capital: Yup.number()
    .required("Tir Capital é obrigatório")
    .typeError("Deve ser um número")
    .moreThan(0, "Tir Capital deve ser maior que 0"),
  zdc_comission: Yup.number()
    .nullable()
    .typeError("Comissão do Zé do Crédito deve ser um número")
    .min(0, "Comissão do Zé do Crédito não pode ser negativa")
    .max(100, "Comissão do Zé do Crédito não pode exceder 100%"),
  faixas_cobertura_seguro: Yup.array().of(
    Yup.object().shape({
      id_empresa_seguro: Yup.string().required("Obrigatório"),
      id_cobertura_produto: Yup.string().required("Obrigatório"),
      tempo_minimo_vinculo: Yup.number().required("Obrigatório"),
      tempo_maximo_vinculo: Yup.number().required("Obrigatório"),
      percentual_seguro: Yup.number().required("Obrigatório"),
    })
  )
});

export default function ConsignadoProductRegisterForm() {
  const [actualProduct, setActualProduct] = useState<
    ConsignadoProduct | undefined
  >(undefined);

  const toast = useToast();
  const navigate = useNavigate();

  const { productId } = useParams();
  const isNew = productId === "new" || productId === "novo";

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!isNew) {
          const product = await getProductById(productId!);

          if (product) {
            setActualProduct(product);
          }
        }
      } catch (err: any) {
        toast({
          title: "Erro ao carregar dados",
          description: err.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    fetchData();
  }, [productId]);

  return (
    <Flex direction="column" width="100%">
      <BackButton routeTo="/consignado/produtos" />

      <Card width="100%" display="flex" flexDir="column">
        <CardHeader width="100%">
          <Heading fontFamily="Lexend Deca" fontSize="3xl" as="b">
            {!isNew ? "Editar Produto" : "Adicionar Produto"}
          </Heading>
        </CardHeader>
        <CardBody height="100%">
          <Formik
            enableReinitialize={true}
            // 1. Novos initialValues baseados nos campos fornecidos
            initialValues={{
              id: actualProduct?.id ?? undefined,
              nome: actualProduct?.nome ?? "",
              comissao: actualProduct?.comissao
                ? parseFloat((actualProduct.comissao * 100).toFixed(3))
                : 0,
              zdc_comission: actualProduct?.zdc_comission
                ? parseFloat(
                    (actualProduct.zdc_comission * 100).toFixed(3)
                  )
                : 0,
              gt_comissao: actualProduct?.gt_comissao
                ? parseFloat((actualProduct.gt_comissao * 100).toFixed(3))
                : 0,
              valor_minimo: actualProduct?.valor_minimo ?? 0,
              valor_maximo_emprestimo: actualProduct?.valor_maximo ?? 0,
              numero_parcelas: actualProduct?.numero_parcelas,
              habilitado: actualProduct?.habilitado ?? true,
              // inadimplencia_esperada: actualProduct?.inadimplencia_esperada
              //   ? parseFloat(
              //       (actualProduct.inadimplencia_esperada * 100).toFixed(3)
              //     )
              //   : 0,
              margem_usada: actualProduct?.porcentual_margem
                ? parseFloat((actualProduct.porcentual_margem * 100).toFixed(3))
                : 0,
              porcentual_seguro: actualProduct?.porcentual_seguro
                ? parseFloat((actualProduct.porcentual_seguro * 100).toFixed(3))
                : 0,
              rebate: actualProduct?.rebate
                ? parseFloat((actualProduct.rebate * 100).toFixed(3))
                : 0,
              taxa_mensal: actualProduct?.taxa_mensal
                ? parseFloat((actualProduct.taxa_mensal * 100).toFixed(3))
                : 0,
              processador: { id: actualProduct?.processador?.id ?? "" },
              cor: actualProduct?.cor ?? "",
              classificacao_de_risco:
                actualProduct?.classificacao_de_risco ?? "",
              cet: actualProduct?.cet
                ? parseFloat((actualProduct?.cet * 100).toFixed(3))
                : 0,
              tir_capital: actualProduct?.tir_capital
                ? parseFloat((actualProduct?.tir_capital * 100).toFixed(3))
                : 0,
              faixas_cobertura_seguro: actualProduct?.faixas_cobertura_seguro?.map((faixa) => ({
                  ...faixa,
                  percentual_seguro: faixa.percentual_seguro 
                    ? parseFloat((faixa.percentual_seguro * 100).toFixed(3))
                    : 0,
                })) || [],
            }}
            validationSchema={productValidationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true);
    
              // 2. Criação do novo ProductData com os novos campos
              const consignadoProductData: Partial<ConsignadoProduct> = {
                nome: values.nome.toUpperCase(), // Campo 'name' mudou para 'nome'
                // Conversão de volta de % para decimal (dividido por 100) para salvar.
                // tac: parseFloat((values.tac / 100).toFixed(3)),
                comissao: values.comissao
                  ? parseFloat((values.comissao / 100).toFixed(3))
                  : undefined,
                // comissao_sem_seguro: values.comissao_sem_seguro
                //   ? parseFloat((values.comissao_sem_seguro / 100).toFixed(3))
                //   : undefined,
                gt_comissao: values.gt_comissao
                  ? parseFloat((values.gt_comissao / 100).toFixed(3))
                  : undefined,
                valor_minimo: parseFloat(values.valor_minimo as any),
                valor_maximo_emprestimo: parseFloat(values.valor_maximo_emprestimo as any),
                valor_maximo: parseFloat(values.valor_maximo_emprestimo as any),
                numero_parcelas: parseInt(
                  values.numero_parcelas as any
                ),
                habilitado: values.habilitado, // Campo 'enabled' mudou para 'habilitado'
                // inadimplencia_esperada: values.inadimplencia_esperada
                //   ? parseFloat((values.inadimplencia_esperada / 100).toFixed(3))
                //   : 0,
                porcentual_margem: values.margem_usada
                  ? parseFloat((values.margem_usada / 100).toFixed(3))
                  : 0,
                porcentual_seguro: values.porcentual_seguro
                  ? parseFloat((values.porcentual_seguro / 100).toFixed(3))
                  : 0,
                rebate: values.rebate
                  ? parseFloat((values.rebate / 100).toFixed(3))
                  : 0,
                taxa_mensal: values.taxa_mensal
                  ? parseFloat((values.taxa_mensal / 100).toFixed(3))
                  : 0,
                processador: { id: "5420465f-9668-4335-9723-a64e7f6b5163" },
                cor: values.cor,
                classificacao_de_risco: values.classificacao_de_risco,
                cet: values.cet
                ? parseFloat((values.cet / 100).toFixed(3))
                : 0,
                tir_capital: values.tir_capital
                ? parseFloat((values.tir_capital / 100).toFixed(3))
                : 0,
                zdc_comission: values.zdc_comission
                ? parseFloat((values.zdc_comission / 100).toFixed(3))
                : undefined,
                faixas_cobertura_seguro: values.faixas_cobertura_seguro?.map((faixa) => ({
                  ...faixa,
                  percentual_seguro: faixa.percentual_seguro 
                    ? parseFloat((Number(faixa.percentual_seguro) / 100).toFixed(3)) 
                    : 0,
                  tempo_minimo_vinculo: Number(faixa.tempo_minimo_vinculo),
                  tempo_maximo_vinculo: Number(faixa.tempo_maximo_vinculo),
                })) || [],
              };

              try {
                !isNew
                  ? await updateProduct(values.id!, consignadoProductData)
                  : await createProduct(consignadoProductData);
                toast({
                  title: `Produto ${!isNew ? "editado" : "cadastrado"} com sucesso`,
                  description: `Produto ${consignadoProductData.nome} foi ${!isNew ? "editado" : "cadastrado"}!`,
                  status: "success",
                  duration: 3000,
                  isClosable: true,
                });

                navigate("/consignado/produtos");
              } catch (error: any) {
                toast({
                  title: "Erro ao salvar produto",
                  description: error.message,
                  status: "error",
                  duration: 3000,
                  isClosable: true,
                });
              }
            }}
          >
            {({ errors, touched, setFieldValue, values, isSubmitting }) => (
              <Form>
                <VStack spacing={8} minW="100%">
                  {/* 3. Ajuste do Layout e dos Campos */}

                  {/* PRIMEIRA LINHA */}
                  <Flex gap={2} width="100%">
                    {/* Nome do Produto (name -> nome) */}
                    <InputField
                      name="nome"
                      label="Nome do Produto"
                      placeholder="Nome do Produto"
                      value={values.nome}
                      error={errors.nome}
                      touched={touched.nome}
                      setFieldValue={setFieldValue}
                      isUpperCase
                    />

                    {/* Comissão (Partner Percentage -> comissao) */}
                    <InputGroup>
                      <InputField
                        name="comissao"
                        label="Comissão (%)"
                        type="number"
                        placeholder="Comissão"
                        value={values.comissao}
                        error={errors.comissao}
                        touched={touched.comissao}
                        setFieldValue={setFieldValue}
                      />
                      <InputRightElement
                        pointerEvents="none"
                        color="black"
                        fontSize="sm"
                        right="0.5rem"
                        top="45%"
                      >
                        %
                      </InputRightElement>
                    </InputGroup>
                    {/* Comissão sem seguro */}
                    <InputGroup>
                      <InputField
                        name="zdc_comission"
                        label="Comissão Zé do Crédito (%)"
                        type="number"
                        placeholder="Comissão Zé do Crédito"
                        value={values.zdc_comission}
                        error={errors.zdc_comission}
                        touched={touched.zdc_comission}
                        setFieldValue={setFieldValue}
                      />
                      <InputRightElement
                        pointerEvents="none"
                        color="black"
                        fontSize="sm"
                        right="0.5rem"
                        top="45%"
                      >
                        %
                      </InputRightElement>
                    </InputGroup>
                    {/* GT Comissão */}
                    <InputGroup>
                      <InputField
                        name="gt_comissao"
                        label="GT Comissão (%)"
                        type="number"
                        placeholder="GT Comissão"
                        value={values.gt_comissao}
                        error={errors.gt_comissao}
                        touched={touched.gt_comissao}
                        setFieldValue={setFieldValue}
                      />
                      <InputRightElement
                        pointerEvents="none"
                        color="black"
                        fontSize="sm"
                        right="0.5rem"
                        top="45%"
                      >
                        %
                      </InputRightElement>
                    </InputGroup>
                    <InputGroup>
                      <InputField
                        name="cor"
                        label="Cor do Produto (HEX)"
                        type="text"
                        placeholder="cor do produto em hexadecimal (#FFFFFF)"
                        value={values.cor}
                        error={errors.cor}
                        touched={touched.cor}
                        setFieldValue={setFieldValue}
                      />
                    </InputGroup>
                    <InputGroup>
                      <RiskClassificationsSelect
                        value={values.classificacao_de_risco}
                        onChange={(val) =>
                          setFieldValue("classificacao_de_risco", val)
                        }
                      />
                    </InputGroup>
                  </Flex>

                  {/* TERCEIRA LINHA - Limites */}
                  <Flex gap="2rem" width="100%">
                    {/* Limite Mínimo (minLimit -> limite_minimo) */}
                    <InputField
                      name="valor_minimo"
                      label="Valor Mínimo (Parcela)"
                      type="text"
                      placeholder="Ex: 1000"
                      value={values.valor_minimo || ""}
                      error={errors.valor_minimo}
                      touched={touched.valor_minimo}
                      setFieldValue={setFieldValue}
                    />

                    {/* Limite Máximo (maxLimit -> limite_maximo) */}
                    <InputField
                      name="valor_maximo_emprestimo"
                      label="Valor Máximo (Parcela)"
                      type="text"
                      placeholder="Ex: 10000"
                      value={values.valor_maximo_emprestimo || ""}
                      error={errors.valor_maximo_emprestimo}
                      touched={touched.valor_maximo_emprestimo}
                      setFieldValue={setFieldValue}
                    />

                    {/* Parcelas Mínimas */}
                    <InputField
                      name="numero_parcelas"
                      label="Quandidade de parcelas"
                      type="number"
                      placeholder="Quantidade de parcelas"
                      value={values.numero_parcelas}
                      error={errors.numero_parcelas}
                      touched={touched.numero_parcelas}
                      setFieldValue={setFieldValue}
                    />

                    {/* Parcelas Máximas */}
                    {/* <InputField
                      name="numero_maximo_parcelas"
                      label="Parcelas Máximas"
                      type="number"
                      placeholder="Parcelas Máximas"
                      value={values.numero_maximo_parcelas}
                      error={errors.numero_maximo_parcelas}
                      touched={touched.numero_maximo_parcelas}
                      setFieldValue={setFieldValue}
                    /> */}
                  </Flex>

         
                  {/* SEXTA LINHA - Rebate/Taxa Mensal/Switch */}
                  <Flex gap="2rem" width="100%" alignItems="center">
                    {/* Rebate */}
                    <InputField
                      name="rebate"
                      label="Rebate (%)"
                      type="number"
                      placeholder="Rebate"
                      value={values.rebate}
                      error={errors.rebate}
                      touched={touched.rebate}
                      setFieldValue={setFieldValue}
                    />


                    {/* Inadimplência Esperada */}
                    {/* <InputField
                      name="inadimplencia_esperada"
                      label="Inadimplência Esperada (%)"
                      type="number"
                      placeholder="Inadimplência Esperada"
                      value={values.inadimplencia_esperada}
                      error={errors.inadimplencia_esperada}
                      touched={touched.inadimplencia_esperada}
                      setFieldValue={setFieldValue}
                    /> */}

                    <InputField
                      name="margem_usada"
                      label="Margem Usada (%)"
                      type="number"
                      placeholder="Margem Usada"
                      value={values.margem_usada}
                      error={errors.margem_usada}
                      touched={touched.margem_usada}
                      setFieldValue={setFieldValue}
                    />

                    {/* <InputField
                      name="porcentual_seguro"
                      label="Seguro (%)"
                      type="number"
                      placeholder="Rebate"
                      value={values.porcentual_seguro}
                      error={errors.porcentual_seguro}
                      touched={touched.porcentual_seguro}
                      setFieldValue={setFieldValue}
                    /> */}
                  </Flex>

                  <Flex gap="2rem" width="100%" alignItems="center">
                    <InputField
                      name="cet"
                      label="CET (%)"
                      type="number"
                      placeholder="CET"
                      value={values.cet}
                      error={errors.cet}
                      touched={touched.cet}
                      setFieldValue={setFieldValue}
                    />

                    <InputField
                      name="tir_capital"
                      label="TIR Capital (%)"
                      type="number"
                      placeholder="TIR Capital"
                      value={values.tir_capital}
                      error={errors.tir_capital}
                      touched={touched.tir_capital}
                      setFieldValue={setFieldValue}
                    />

                    <InputField
                      name="taxa_mensal"
                      label="Taxa Mensal (%)"
                      type="number"
                      placeholder="Taxa Mensal"
                      value={values.taxa_mensal}
                      error={errors.taxa_mensal}
                      touched={touched.taxa_mensal}
                      setFieldValue={setFieldValue}
                    />

                 
                    {/* Ativo (enabled -> habilitado) */}
                    <Flex
                      gap="1rem"
                      alignItems="center"
                      pt="1.75rem"
                      width="33.33%"
                    >
                      <FormLabel htmlFor="habilitado" mb="0">
                        Ativo
                      </FormLabel>
                      <Switch
                        id="habilitado"
                        name="habilitado"
                        isChecked={values.habilitado}
                        onChange={(e) =>
                          setFieldValue("habilitado", e.target.checked)
                        }
                        colorScheme="green"
                      />
                    </Flex>
                  </Flex>

                  <Divider my={6} />
                  <Flex width="100%" align="center" justify="start" mb={4} gap={6}>
                    <Heading fontSize="3xl">Seguradoras</Heading>
                    <Button 
                      leftIcon={<AddIcon />} 
                      bgColor="#5fbd44"
                      _hover={{
                        bgColor: "#54A23F",
                      }}
                      size="sm"
                      onClick={() => {
                        const current = values.faixas_cobertura_seguro || [];
                        setFieldValue("faixas_cobertura_seguro", [
                          ...current, 
                          { id_empresa_seguro: "", id_cobertura_produto: "", tempo_minimo_vinculo: "", tempo_maximo_vinculo: "", percentual_seguro: "" }
                        ]);
                      }}
                    >
                      Adicionar
                    </Button>
                  </Flex>

                  <FieldArray name="faixas_cobertura_seguro">
                    {() => (
                      <VStack spacing={4} width="100%" align="stretch">
                        {values.faixas_cobertura_seguro?.map((_: any, index: number) => (
                          <Flex 
                            key={index} 
                            gap={4} 
                            p={4} 
                            border="1px solid" 
                            borderColor="gray.200" 
                            borderRadius="md" 
                            alignItems="flex-end"
                            bg="gray.50"
                          >
                            <VStack align="start" flex={1}>
                              <InputField
                                name={`faixas_cobertura_seguro.${index}.tempo_minimo_vinculo`}
                                label="Tempo mín. vínculo"
                                placeholder="0"
                                value={values.faixas_cobertura_seguro[index].tempo_minimo_vinculo}
                                error={getIn(errors, `faixas_cobertura_seguro.${index}.tempo_minimo_vinculo`)}
                                touched={getIn(touched, `faixas_cobertura_seguro.${index}.tempo_minimo_vinculo`)}
                                setFieldValue={setFieldValue}
                              />
                            </VStack>
                            
                            <VStack align="start" flex={1}>
                              <InputField
                                name={`faixas_cobertura_seguro.${index}.tempo_maximo_vinculo`}
                                label="Tempo máx. vínculo"
                                placeholder="0"
                                value={values.faixas_cobertura_seguro[index].tempo_maximo_vinculo}
                                error={getIn(errors, `faixas_cobertura_seguro.${index}.tempo_maximo_vinculo`)}
                                touched={getIn(touched, `faixas_cobertura_seguro.${index}.tempo_maximo_vinculo`)}
                                setFieldValue={setFieldValue}
                              />
                            </VStack>

                            <Box flex={2}>
                              <InsuranceCompaniesSelect
                                value={values.faixas_cobertura_seguro[index].id_empresa_seguro}
                                error={getIn(errors, `faixas_cobertura_seguro.${index}.id_empresa_seguro`)}
                                touched={getIn(touched, `faixas_cobertura_seguro.${index}.id_empresa_seguro`)}
                                onChange={(val) => {
                                  setFieldValue(`faixas_cobertura_seguro.${index}.id_empresa_seguro`, val);
                                  setFieldValue(`faixas_cobertura_seguro.${index}.id_cobertura_produto`, "");
                                }}
                              />
                            </Box>

                            <Box flex={2}>
                              <InsuranceCompanyProductsSelect
                                value={values.faixas_cobertura_seguro[index].id_cobertura_produto}
                                selectedInsuranceCompanyId={values.faixas_cobertura_seguro[index].id_empresa_seguro}
                                error={getIn(errors, `faixas_cobertura_seguro.${index}.id_cobertura_produto`)}
                                touched={getIn(touched, `faixas_cobertura_seguro.${index}.id_cobertura_produto`)}
                                onChange={(val) => setFieldValue(`faixas_cobertura_seguro.${index}.id_cobertura_produto`, val)}
                              />
                            </Box>

                            <VStack align="start" flex={1}>
                              <InputField
                                name={`faixas_cobertura_seguro.${index}.percentual_seguro`}
                                label="Seguro (%)"
                                placeholder="0"
                                value={values.faixas_cobertura_seguro[index].percentual_seguro}
                                error={getIn(errors, `faixas_cobertura_seguro.${index}.percentual_seguro`)}
                                touched={getIn(touched, `faixas_cobertura_seguro.${index}.percentual_seguro`)}
                                setFieldValue={setFieldValue}
                              />
                            </VStack>

                            <IconButton
                              aria-label="Remover"
                              icon={<DeleteIcon />}
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => {
                                const newArr = [...values.faixas_cobertura_seguro];
                                newArr.splice(index, 1);
                                setFieldValue("faixas_cobertura_seguro", newArr);
                              }}
                            />
                          </Flex>
                        ))}
                      </VStack>
                    )}
                  </FieldArray>

                  {/* Botões de Ação */}
                  <Flex justify="center" gap="2rem">
                    {!isNew && (
                      <DeleteButton
                        updateService={updateProduct}
                        id={values.id!}
                        navigateRoute="/consignado/produtos"
                        entityName="Produto"
                        entityIdentifier={values.nome} // Campo 'name' mudou para 'nome'
                        buttonProps={{
                          isLoading: isSubmitting,
                          loadingText: "Desativando",
                          style: {
                            backgroundColor: "red",
                            color: "white",
                            width: "16rem",
                          },
                        }}
                      />
                    )}

                    <Button
                      color={"white"}
                      type="submit"
                      bg={"secondary"}
                      _hover={{ bg: "#1e3f5b" }}
                      width="16rem"
                      isLoading={isSubmitting}
                      loadingText="Salvando"
                    >
                      {!isNew ? "Salvar Alterações" : "Cadastrar Produto"}
                    </Button>
                  </Flex>
                </VStack>
              </Form>
            )}
          </Formik>
        </CardBody>
      </Card>
    </Flex>
  );
}
